import {normalizePage} from "@/lib/paging.ts";
import api from "@/lib/api.ts";
import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {bannerKeys} from "@/hooks/useBanners.ts";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type TagId = string;

export type Tag = {
    id: TagId;
    name: string;
    description?: string | null;
    slug?: string;
};

export type SaveTagInput = {
    id?: TagId;
    name: string;
    description?: string | null;
};

export type SortField = "name";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
};

export type TagPage = ReturnType<typeof normalizePage<Tag>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/tags";
const EDITOR_BASE = "/api/v1/editor/tags";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "title" as SortField,
    sortOrder: "desc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const tagKeys = {
    all: ["tags"] as const,
    list: (params?: HookParams) => [...tagKeys.all, "list", params ?? {}] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): TagPage {
    return normalizePage<Tag>({page, size, total: 0, content: []});
}

async function fetchTags(params: HookParams = {}): Promise<TagPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(PUBLIC_BASE, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<Tag>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchTagBySlug(slug: string): Promise<Tag | null> {
    return await api.get(`${PUBLIC_BASE}/${slug}`)
        .then(({data}) => data as Tag)
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useTags(params: HookParams = {}) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: tagKeys.list(q),
        queryFn: () => fetchTags(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useTagBySlug(slug: string) {
    return useQuery({
        queryKey: [...tagKeys.all, "slug", slug],
        queryFn: () => fetchTagBySlug(slug),
        enabled: !!slug,
        staleTime: 5 * 60_000,
        gcTime: 15 * 60_000,
        refetchOnWindowFocus: false,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveTag() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...tagKeys.all, "save"],
        mutationFn: async (payload: SaveTagInput): Promise<Tag> => {
            const body = {
                name: payload.name?.trim(),
                description: payload.description?.toString().trim() || undefined,
            };

            if (payload.id) {
                const {data} = await api.put(`${EDITOR_BASE}/${payload.id}`, body);
                return data;
            } else {
                const {data} = await api.post(EDITOR_BASE, body);
                return data;
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: tagKeys.all});
        },
    });
}

export function useDeleteTag() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...tagKeys.all, "delete"],
        mutationFn: async (id: TagId) => {
            await api.delete(`${EDITOR_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            // Cancel outgoing refetches so we don't overwrite our optimistic update
            await qc.cancelQueries({queryKey: tagKeys.all});

            // Snapshot all related queries
            const snapshots = qc.getQueriesData<unknown>({queryKey: tagKeys.all});

            // Optimistically remove the banner from any paged lists we find
            snapshots.forEach(([key, value]) => {
                const page = value as Partial<TagPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: TagPage = {
                        ...(page as TagPage),
                        items: page.items.filter((b: any) => b.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            // Roll back from snapshots if we have them
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            // Ensure server truth wins in the end
            qc.invalidateQueries({queryKey: bannerKeys.all});
        },
    });
}
